// ============================================================
// CharacterExportDialog - take characters out of a session as they stand.
//
// One dialog for the three things asked of it: a file for one character
// (or a team, or everyone), a save of each to the library "as they
// stand", and a readable copy to paste or print. Who is included is a
// tick list, so "the end-of-session snapshot" is the same dialog with
// the player characters ticked.
//
// Importing the file brings them back BESIDE the roster (see
// useShellImports): a character file never replaces a table.
// ============================================================
import { useMemo, useState } from 'react';
import { useRosterContext } from '../contexts/RosterContext';
import { characterToText } from '../engine/character/sheetText';
import { characterFileName, toCharacterFile, type CharacterExportOptions } from '../engine/roster/characterFile';
import { projectDefender, sheetArmour } from '../engine/roster/projection';
import { readyForSheetEdit } from '../engine/roster/sheetEdit';
import { standingSummary, withStanding } from '../engine/roster/standing';
import type { Combatant } from '../engine/roster/types';
import useModalBackdrop from '../hooks/useModalBackdrop';
import useEscapeKey from '../hooks/useEscapeKey.js';
import common from '../styles/common.module.css';
import styles from '../styles/ImportExportDialog.module.css';
import clsx from '../utils/clsx';
import copyText from '../utils/copyText';
import downloadJson from '../utils/downloadJson';
import printText from '../utils/printText';

interface Props {
  /** Who is ticked when the dialog opens. */
  initialIds: string[];
  /** False while sheets are locked to this viewer: the library save is a sheet write like any other. */
  canSaveToLibrary?: boolean;
  onClose: () => void;
}

type OptionKey = keyof CharacterExportOptions;

/** What the file may carry, in the order the dialog lists it. */
const OPTIONS: { key: OptionKey; label: string; needsState?: boolean }[] = [
  { key: 'includeState', label: 'Current state: wounds, conditions, critical damage, cover' },
  { key: 'includeAmmo', label: 'Ammunition left in each weapon', needsState: true },
  { key: 'includeWornArmour', label: 'Worn armour', needsState: true },
  { key: 'includeNarrative', label: 'Their narrative log' },
];

const DEFAULTS: CharacterExportOptions = {
  includeState: true,
  includeAmmo: true,
  includeWornArmour: true,
  includeNarrative: false,
};

/** The characters as text, one after another, each as they stand. */
function textOf(chosen: Combatant[]): string {
  // Pins speak over the sheet: the plates and the wounds maximum in force.
  const one = (c: Combatant) =>
    characterToText(c.sheet, c.state, { armour: sheetArmour(c), woundsMax: projectDefender(c).wounds.max });
  return chosen.map(one).join('\n\n----------------------------------------\n\n');
}

interface WhoProps {
  /** The roster as the sheet shows it: a statblock's pins folded in. */
  roster: Combatant[];
  ticked: Set<string>;
  onTicked: (next: Set<string>) => void;
}

/** The tick list of who goes into the file, with two shortcuts. */
function Who({ roster, ticked, onTicked }: WhoProps) {
  const flip = (id: string) => {
    const next = new Set(ticked);
    if (!next.delete(id)) next.add(id);
    onTicked(next);
  };
  const characters = roster.filter((c) => c.kind === 'character').map((c) => c.id);
  return (
    <>
      <div className={styles.sectionRow}>
        <button type="button" className={common.btnSmall} onClick={() => onTicked(new Set(characters))}>
          All characters ({characters.length})
        </button>
        <button type="button" className={common.btnSmall} onClick={() => onTicked(new Set(roster.map((c) => c.id)))}>
          Everyone ({roster.length})
        </button>
      </div>
      {roster.map((c) => (
        <div key={c.id} className={styles.sectionRow}>
          <input
            className={styles.checkbox}
            type="checkbox"
            id={`charexp-${c.id}`}
            data-testid={`charexp-who-${c.id}`}
            checked={ticked.has(c.id)}
            onChange={() => flip(c.id)}
          />
          <label className={styles.sectionLabel} htmlFor={`charexp-${c.id}`}>
            {c.name}
          </label>
          <span className={styles.sectionCount}>{standingSummary(withStanding(c))}</span>
        </div>
      ))}
    </>
  );
}

interface WhatProps {
  options: CharacterExportOptions;
  onOptions: (next: CharacterExportOptions) => void;
}

/** The tick list of what the file carries. */
function What({ options, onOptions }: WhatProps) {
  return (
    <>
      {OPTIONS.map(({ key, label, needsState }) => {
        const off = !!needsState && !options.includeState;
        return (
          <div key={key} className={styles.sectionRow}>
            <input
              className={styles.checkbox}
              type="checkbox"
              id={`charexp-${key}`}
              data-testid={`charexp-${key}`}
              checked={!!options[key] && !off}
              disabled={off}
              onChange={() => onOptions({ ...options, [key]: !options[key] })}
            />
            <label className={styles.sectionLabel} htmlFor={`charexp-${key}`}>
              {label}
            </label>
          </div>
        );
      })}
    </>
  );
}

interface ActionsProps {
  /** Nobody is ticked: there is nothing to act on. */
  none: boolean;
  onFile: () => void;
  /** Null while the viewer may not write sheets to the library. */
  onLibrary: (() => void) | null;
  onCopy: () => void;
  onPrint: () => void;
  onClose: () => void;
}

/** The dialog's verbs. */
function Actions({ none, onFile, onLibrary, onCopy, onPrint, onClose }: ActionsProps) {
  const verbs: [string, string, (() => void) | null][] = [
    ['charexp-library', 'Save to library as they stand', onLibrary],
    ['charexp-copy', 'Copy as text', onCopy],
    ['charexp-print', 'Print', onPrint],
  ];
  return (
    <div className={clsx(styles.sectionRow, styles.actions)}>
      <button type="button" className={common.btnPrimary} data-testid="charexp-file" disabled={none} onClick={onFile}>
        Export file
      </button>
      {verbs.map(([testId, label, run]) => (
        <button
          key={testId}
          type="button"
          className={common.btnSecondary}
          data-testid={testId}
          disabled={none || !run}
          title={run ? undefined : 'Sheets are locked'}
          onClick={run ?? undefined}
        >
          {label}
        </button>
      ))}
      <button type="button" className={common.btnSecondary} onClick={onClose}>
        Close
      </button>
    </div>
  );
}

export default function CharacterExportDialog(props: Props) {
  const { initialIds, canSaveToLibrary = true, onClose } = props;
  const { roster, onSaveStanding } = useRosterContext();
  // As the sheet shows them: a statblock's pins folded into its sheet.
  const shown = useMemo(() => (roster as Combatant[]).map(readyForSheetEdit), [roster]);
  const backdrop = useModalBackdrop(onClose);
  useEscapeKey(true, onClose); // Escape cancels, as a click outside does (2026-10-23).
  const [ticked, setTicked] = useState(() => new Set(initialIds));
  const [options, setOptions] = useState(DEFAULTS);
  const [said, setSaid] = useState('');
  const chosen = shown.filter((c) => ticked.has(c.id));
  const none = chosen.length === 0;
  const exportFile = () => {
    downloadJson(toCharacterFile(chosen, options), characterFileName(chosen));
    setSaid(`Exported ${chosen.length} to a file.`);
  };
  const saveStanding = () => {
    onSaveStanding(chosen.map((c) => c.id));
    setSaid(`Saved ${chosen.length} to the library as they stand.`);
  };
  const copy = async () => setSaid((await copyText(textOf(chosen))) ? 'Copied as text.' : 'Could not copy.');
  return (
    <div className={common.modal} onClick={backdrop.onBackdropClick} role="presentation">
      <div
        className={common.modalContent}
        ref={backdrop.boxRef}
        role="dialog"
        aria-modal="true"
        aria-label="Export characters"
      >
        <h2 className={styles.title}>Export characters</h2>
        <Who roster={shown} ticked={ticked} onTicked={setTicked} />
        <h3 className={styles.subtitle}>The file carries</h3>
        <What options={options} onOptions={setOptions} />
        <div className={styles.sectionRow} role="status" data-testid="charexp-said">
          {said}
        </div>
        <Actions
          none={none}
          onFile={exportFile}
          onLibrary={canSaveToLibrary && onSaveStanding ? saveStanding : null}
          onCopy={copy}
          onPrint={() => printText('Characters', textOf(chosen))}
          onClose={onClose}
        />
      </div>
    </div>
  );
}
